import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import {
  ContactCompany,
  ContactCompanyActivity,
  ContactCompanyActivityType,
  ContactCompanyMember,
  ContactCompanyOwnerId,
  ContactCompanyRole,
  normalizeContactCompanyDropoffLocationIds,
  normalizeContactCompanyTerminalIds,
  type CrmActivityDomain,
} from "../leads/contactCompanies";
import { locationIdForFirestore, parseStoredLocationId } from "./locationId";

const VALID_ROLES: ContactCompanyRole[] = ["owner", "manager", "partner"];

const VALID_ACTIVITY_TYPES: ContactCompanyActivityType[] = [
  "call",
  "sms",
  "email",
  "note",
  "task",
  "lead",
  "shipping_repairs",
];

const parseActivityDomain = (v: unknown): CrmActivityDomain | undefined =>
  v === "inventory" || v === "sales" || v === "marketing" ? v : undefined;

const convertTimestamp = (timestamp: unknown): Date | null => {
  if (!timestamp) return null;
  if (timestamp instanceof Timestamp) return timestamp.toDate();
  if (timestamp instanceof Date) return Number.isNaN(timestamp.getTime()) ? null : timestamp;
  if (typeof timestamp === "string") {
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const serializeDate = (d: Date): string =>
  d instanceof Date && !Number.isNaN(d.getTime()) ? d.toISOString() : new Date().toISOString();

const serializeDateNullable = (d: Date | null | undefined): string | null => {
  if (!d) return null;
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const parseOwnerId = (v: unknown): ContactCompanyOwnerId => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") return v;
  return null;
};

const parseRole = (v: unknown): ContactCompanyRole =>
  typeof v === "string" && (VALID_ROLES as string[]).includes(v)
    ? (v as ContactCompanyRole)
    : "manager";

const parseActivityType = (v: unknown): ContactCompanyActivityType | null =>
  typeof v === "string" && (VALID_ACTIVITY_TYPES as string[]).includes(v)
    ? (v as ContactCompanyActivityType)
    : null;

const parseMembers = (raw: unknown): ContactCompanyMember[] => {
  if (!Array.isArray(raw)) return [];
  const out: ContactCompanyMember[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const contactId = typeof e.contactId === "string" ? e.contactId.trim() : "";
    if (!contactId || seen.has(contactId)) continue;
    seen.add(contactId);
    const role = parseRole(e.role);
    const addedAt = convertTimestamp(e.addedAt) ?? new Date();
    out.push({ contactId, role, addedAt });
  }
  return out;
};

const serializeMembers = (members: ContactCompanyMember[] | undefined) => {
  if (!Array.isArray(members)) return [];
  const seen = new Set<string>();
  return members
    .map((m) => {
      const contactId = typeof m?.contactId === "string" ? m.contactId.trim() : "";
      if (!contactId || seen.has(contactId)) return null;
      seen.add(contactId);
      return {
        contactId,
        role: parseRole(m?.role),
        addedAt: serializeDate(m?.addedAt instanceof Date ? m.addedAt : new Date()),
      };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);
};

const parseActivities = (raw: unknown): ContactCompanyActivity[] => {
  if (!Array.isArray(raw)) return [];
  const out: ContactCompanyActivity[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const type = parseActivityType(e.type);
    if (!type) continue;
    const createdAt = convertTimestamp(e.createdAt);
    if (!createdAt) continue;
    const note = typeof e.note === "string" ? e.note : "";
    const createdByUserId =
      typeof e.createdByUserId === "string" ? e.createdByUserId : "";
    const taskId = typeof e.taskId === "string" && e.taskId ? e.taskId : undefined;
    const leadId = typeof e.leadId === "string" && e.leadId ? e.leadId : undefined;
    const contactId =
      typeof e.contactId === "string" && e.contactId ? e.contactId : undefined;
    const domain = parseActivityDomain(e.domain);
    const activity: ContactCompanyActivity = {
      type,
      note,
      createdAt,
      createdByUserId,
      ...(taskId ? { taskId } : {}),
      ...(leadId ? { leadId } : {}),
      ...(contactId ? { contactId } : {}),
      ...(domain ? { domain } : {}),
    };
    out.push(activity);
  }
  return out;
};

const serializeActivities = (activities: ContactCompanyActivity[] | undefined) => {
  if (!Array.isArray(activities)) return [];
  return activities
    .map((a) => {
      const type = parseActivityType(a?.type);
      if (!type) return null;
      const createdAt =
        a?.createdAt instanceof Date && !Number.isNaN(a.createdAt.getTime())
          ? a.createdAt.toISOString()
          : new Date().toISOString();
      const domain = parseActivityDomain(a.domain);
      return {
        type,
        note: typeof a.note === "string" ? a.note : "",
        ...(a.taskId ? { taskId: a.taskId } : {}),
        ...(a.leadId ? { leadId: a.leadId } : {}),
        ...(a.contactId ? { contactId: a.contactId } : {}),
        ...(domain ? { domain } : {}),
        createdAt,
        createdByUserId:
          typeof a.createdByUserId === "string" ? a.createdByUserId : "",
      };
    })
    .filter((v): v is NonNullable<typeof v> => v !== null);
};

export const contactCompanyConverter: FirestoreDataConverter<ContactCompany> = {
  toFirestore(company: ContactCompany) {
    const { id: _id, ...rest } = company;
    const businessAddressId = locationIdForFirestore(rest.businessAddressId);
    const dropoffLocationIds = normalizeContactCompanyDropoffLocationIds(
      rest.dropoffLocationIds,
    );
    const terminalIds = normalizeContactCompanyTerminalIds(rest.terminalIds);
    return {
      name: rest.name?.trim() ?? "",
      ...(rest.website?.trim() ? { website: rest.website.trim() } : {}),
      ...(rest.notes?.trim() ? { notes: rest.notes.trim() } : {}),
      ownerId: rest.ownerId === undefined ? null : rest.ownerId,
      businessAddressId,
      dropoffLocationIds,
      terminalIds,
      members: serializeMembers(rest.members),
      lastActivityAt: serializeDateNullable(rest.lastActivityAt ?? null),
      activities: serializeActivities(rest.activities),
      createdAt: serializeDate(rest.createdAt),
      updatedAt: serializeDate(rest.updatedAt),
      createdByUserId: rest.createdByUserId ?? "",
    };
  },

  fromFirestore(snapshot: QueryDocumentSnapshot, options?: SnapshotOptions): ContactCompany {
    const data = snapshot.data(options) ?? {};
    const businessAddressId = parseStoredLocationId(
      data.businessAddressId,
      data.businessAddress,
    );
    const dropoffLocationIds = normalizeContactCompanyDropoffLocationIds(
      data.dropoffLocationIds,
      parseStoredLocationId(data.dropoffLocationId, data.dropoffLocation),
    );
    const terminalIds = normalizeContactCompanyTerminalIds(data.terminalIds);

    const websiteRaw = typeof data.website === "string" ? data.website.trim() : "";
    const notesRaw = typeof data.notes === "string" ? data.notes.trim() : "";

    return {
      id: snapshot.id,
      name: typeof data.name === "string" ? data.name : "",
      ...(websiteRaw ? { website: websiteRaw } : {}),
      ...(notesRaw ? { notes: notesRaw } : {}),
      ownerId: parseOwnerId(data.ownerId),
      ...(businessAddressId ? { businessAddressId } : {}),
      dropoffLocationIds,
      terminalIds,
      members: parseMembers(data.members),
      lastActivityAt: convertTimestamp(data.lastActivityAt),
      activities: parseActivities(data.activities),
      createdAt: convertTimestamp(data.createdAt) || new Date(),
      updatedAt: convertTimestamp(data.updatedAt) || new Date(),
      createdByUserId: typeof data.createdByUserId === "string" ? data.createdByUserId : "",
    };
  },
};
